import { normalizarDecimal, precioValido } from "@/componentes/ordenes/lineas"
import { aCentimos } from "./dinero"

/**
 * Convertir entre monedas como se convierte en Venezuela.
 *
 * ⚠ **La tasa se lee siempre como BOLÍVARES POR UNA UNIDAD**, que es como se habla
 * —«el dólar está a 848»— y como se hace la cuenta a mano: la moneda extranjera se
 * MULTIPLICA por su valor en bolívares. 1.000 $ × 848,5458 = 848.545,80 Bs. La vuelta
 * divide. El ledger guarda otra convención —«unidades por 1 USD», regla 20— y la
 * traduce el servidor en `panel_tasas.vigentes_en_bolivares`: aquí no se vuelve a
 * convertir, que sería una segunda copia de la regla en otro idioma.
 *
 * ⚠ **El bolívar es el eje, y no por gusto.** Las tasas viven contra el bolívar, así
 * que pasar de euros a dólares es euros → bolívares → dólares. Es la misma cuenta que
 * haría una persona con la calculadora delante del extracto.
 *
 * ⚠ **Todo en enteros.** Los importes en céntimos y las tasas en millonésimas, igual
 * que `Numeric(18,6)` en la base. Un `number` de JavaScript pierde céntimos en cuanto
 * el importe pasa de unos millones de bolívares, que con esta moneda es una factura
 * de teléfono.
 */

/** Millonésimas de bolívar por unidad: `848,5458` → `848545800n`. */
export type TasaMicro = bigint

const MICRO = 1_000_000n

/** Redondeo a la mitad hacia arriba sobre enteros: el mismo del servidor. */
function dividirRedondeando(numerador: bigint, denominador: bigint): bigint {
  if (denominador <= 0n) return 0n
  return (numerador * 2n + denominador) / (denominador * 2n)
}

/**
 * Una tasa tecleada, a millonésimas.
 *
 * `null` si no es un número, si trae más de seis decimales o si no es positiva: una
 * tasa cero o negativa no convierte, sólo produce ceros en silencio (regla 21).
 */
export function aTasaMicro(texto: string): TasaMicro | null {
  const limpio = normalizarDecimal(texto)
  if (limpio === null) return null
  const [entera = "0", decimal = ""] = limpio.split(".")
  if (decimal.length > 6) return null
  const micro = BigInt(entera) * MICRO + BigInt(decimal.padEnd(6, "0"))
  return micro > 0n ? micro : null
}

/** `848545800n` → `"848,5458"`, sin los ceros de relleno de la derecha. */
export function comoTasa(micro: TasaMicro): string {
  const entera = (micro / MICRO).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".")
  const decimal = (micro % MICRO).toString().padStart(6, "0").replace(/0+$/, "")
  return decimal === "" ? entera : `${entera},${decimal}`
}

/**
 * El importe MULTIPLICADO por la tasa: la cuenta natural.
 *
 * Con la tasa leída como «bolívares por 1 euro», 300 € × 974,4193 son bolívares. Con
 * una tasa entre dos divisas —«dólares por 1 euro»— es la misma operación: la tasa
 * dice en qué unidades sale el resultado.
 */
export function porTasa(centimos: bigint, tasa: TasaMicro): bigint {
  return dividirRedondeando(centimos * tasa, MICRO)
}

/** La vuelta: el importe DIVIDIDO entre la tasa. De bolívares a euros. */
export function entreTasa(centimos: bigint, tasa: TasaMicro): bigint {
  return dividirRedondeando(centimos * MICRO, tasa)
}

/**
 * La tasa entre dos monedas a partir de lo que vale cada una en bolívares.
 *
 * «Dólares por 1 euro» sale de dividir los dos precios en bolívares: 974,4193 ÷
 * 848,5458 = 1,148406. Con el bolívar como principal la división es por 1 y queda la
 * lectura de siempre, que es justo lo que se quiere: una sola fórmula para los dos
 * casos, y no un `if` que alguien tendrá que mantener de acuerdo con el otro.
 */
export function tasaRelativa(bsReferencia: TasaMicro, bsPrincipal: TasaMicro): TasaMicro {
  return dividirRedondeando(bsReferencia * MICRO, bsPrincipal)
}

/**
 * La tasa que explica dos importes: bolívares ÷ moneda extranjera.
 *
 * Es la tasa REALMENTE aplicada en una operación —lo que costó el cambio—, que casi
 * nunca es la del BCV. `null` si alguno de los dos no es positivo.
 */
export function tasaEntre(centimosBs: bigint, centimosMoneda: bigint): TasaMicro | null {
  if (centimosBs <= 0n || centimosMoneda <= 0n) return null
  return dividirRedondeando(centimosBs * MICRO, centimosMoneda)
}

/**
 * Céntimos al texto que espera un campo: `123456n` → `"1234.56"`.
 *
 * Con punto y sin separador de miles a propósito: es lo que `precioValido` acepta y
 * lo que se manda al servidor. Lo bonito —«1.234,56»— es `comoMonto`, y ése es para
 * leer, no para escribir.
 */
export function comoCampo(centimos: bigint): string {
  const negativo = centimos < 0n
  const abs = negativo ? -centimos : centimos
  return `${negativo ? "-" : ""}${abs / 100n}.${(abs % 100n).toString().padStart(2, "0")}`
}

/** Lo que hay tecleado en un campo de importe, en céntimos. `null` si no vale. */
export function centimosDe(texto: string): bigint | null {
  const valido = precioValido(texto)
  return valido === null ? null : aCentimos(valido)
}

// --- Importes con más de dos decimales -----------------------------------------
//
// ⚠ **Se ADMITEN hasta cinco decimales y se REGISTRAN con dos** (lo pidió el usuario el
// 06/10). Binance debita «297,027 USDT» y obligar a teclear 297,03 hace la cuenta con
// un importe que no es el que salió: a 996,7503 son 3 Bs de diferencia en lo que entra.
// Así que la conversión se hace con TODOS los decimales tecleados y sólo el resultado
// se redondea a céntimos. Lo que se guarda sigue siendo `DINERO` —`Numeric(18, 2)`—, y
// la diferencia de 0,003 contra el extracto de Binance la avisa el formulario.

/** Cien milésimas: la unidad de cálculo de un importe tecleado con hasta 5 decimales. */
const ESCALA = 100_000n
export const DECIMALES_MAXIMOS = 5

/** `"297,027"` → `29702700n` (cien milésimas). `null` si no vale o no es positivo. */
export function unidadesDe(texto: string): bigint | null {
  const limpio = normalizarDecimal(texto)
  if (limpio === null) return null
  const [entera = "0", decimal = ""] = limpio.split(".")
  if (decimal.length > DECIMALES_MAXIMOS) return null
  const unidades = BigInt(entera) * ESCALA + BigInt(decimal.padEnd(DECIMALES_MAXIMOS, "0"))
  return unidades > 0n ? unidades : null
}

/** Cien milésimas a céntimos, media unidad hacia arriba: 297,027 → 297,03. */
export function aCentimosRedondeando(unidades: bigint): bigint {
  return dividirRedondeando(unidades, ESCALA / 100n)
}

/** Céntimos a cien milésimas, para volver a entrar en una cuenta. */
export function deCentimos(centimos: bigint): bigint {
  return centimos * (ESCALA / 100n)
}

/** Importe × tasa, con todos los decimales; el resultado, en céntimos. */
export function porTasaPreciso(unidades: bigint, tasa: TasaMicro): bigint {
  return dividirRedondeando(unidades * tasa, MICRO * (ESCALA / 100n))
}

/** Importe ÷ tasa, con todos los decimales; el resultado, en céntimos. */
export function entreTasaPreciso(unidades: bigint, tasa: TasaMicro): bigint {
  return dividirRedondeando(unidades * MICRO, tasa * (ESCALA / 100n))
}

/** La tasa que explica dos importes, los dos en cien milésimas. */
export function tasaEntrePreciso(unidadesBs: bigint, unidadesMoneda: bigint): TasaMicro | null {
  if (unidadesBs <= 0n || unidadesMoneda <= 0n) return null
  return dividirRedondeando(unidadesBs * MICRO, unidadesMoneda)
}

/** Lo que se manda al servidor: el importe tecleado, redondeado a dos decimales. */
export function paraRegistrar(texto: string): string {
  const unidades = unidadesDe(texto)
  return unidades === null ? "" : comoCampo(aCentimosRedondeando(unidades))
}

/** Por qué un importe no vale, en palabras; `null` si vale o si está vacío. */
export function motivoImporte(texto: string): string | null {
  if (texto.trim() === "") return null
  const limpio = normalizarDecimal(texto)
  if (limpio === null) return "No es un número: coma o punto para los decimales."
  if ((limpio.split(".")[1]?.length ?? 0) > DECIMALES_MAXIMOS) {
    return `Hasta ${DECIMALES_MAXIMOS} decimales.`
  }
  return unidadesDe(texto) === null ? "Tiene que ser mayor que cero." : null
}

/** Si el importe trae más de dos decimales, cómo queda registrado. */
export function notaRedondeo(texto: string): string | null {
  const limpio = normalizarDecimal(texto)
  const unidades = unidadesDe(texto)
  if (limpio === null || unidades === null || (limpio.split(".")[1]?.length ?? 0) <= 2) {
    return null
  }
  return `Se registra como ${comoCampo(aCentimosRedondeando(unidades)).replace(".", ",")}: el sistema guarda dos decimales.`
}

/** Por qué una tasa no vale; `null` si vale o si está vacía. */
export function motivoTasa(texto: string): string | null {
  if (texto.trim() === "") return null
  return aTasaMicro(texto) === null ? "Hasta seis decimales, y mayor que cero." : null
}
